import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { LocalStorageService } from '../../local-storage/local-storage.service';

// -----------------------------
// VALIDATORS CUSTOM (fuera de la clase)
// -----------------------------

/** Al menos una fila debe tener "ingresos" y al menos una debe tener "egresos". */
function rangosAtLeastOneValidator(): ValidatorFn {
  return (array: AbstractControl): ValidationErrors | null => {
    const rows = (array as FormArray).value as any[];
    const ingresosSelected = rows.some((r) => r.ingresos);
    const egresosSelected = rows.some((r) => r.egresos);

    const errors: ValidationErrors = {};
    if (!ingresosSelected) {
      errors['ingresosRequired'] = true;
    }
    if (!egresosSelected) {
      errors['egresosRequired'] = true;
    }
    return Object.keys(errors).length ? errors : null;
  };
}

/** Si "realizaOperaciones" es true, exige al menos una operación marcada. */
function monedaExtranjeraValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const g = group as FormGroup;
    const realiza = g.get('realizaOperaciones')?.value;

    if (!realiza) {
      return null;
    }

    const operaciones = [
      'exportaciones',
      'importaciones',
      'transferencias',
      'pagosServicios',
      'inversiones',
      'prestamos',
      'giros',
    ];

    const algunaSeleccionada = operaciones.some(
      (op) => g.get(op)?.value === true,
    );

    return algunaSeleccionada ? null : { operacionRequired: true };
  };
}

@Component({
  selector: 'app-preguntas-sarlaft',
  templateUrl: './preguntas-sarlaft.component.html',
  styleUrls: ['./preguntas-sarlaft.component.css'],
})
export class PreguntasSarlaftComponent implements OnInit {
  formFinanciero!: FormGroup;
  @Output() estadoValido = new EventEmitter<boolean>();

  // -----------------------------
  // ERRORES VISUALES SARLAFT
  // -----------------------------
  // Se activan en la primera interacción del usuario con cada bloque,
  // no dependen de un submit (no existe botón que lo dispare).
  showRangeErrors = false;
  showMonedaErrors = false;

  rangos = [
    'Menor a $1.5 millones',
    '$1.5 - $3 millones',
    '$3 - $5 millones',
    '$5 - $8 millones',
    'Mayores a $8 millones',
  ];

  constructor(
    private fb: FormBuilder,
    private localStorageService: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.listenOtrosOrigen();
    this.listenMonedaExtranjera();
    this.listenPep();

    this.restoreForm();

    this.formFinanciero.statusChanges.subscribe(() => {
      this.estadoValido.emit(this.formFinanciero.valid);
    });

    this.estadoValido.emit(this.formFinanciero.valid);

    this.persistFormChanges();
  }

  get form(): FormGroup {
    return this.formFinanciero;
  }

  // --------------------------------
  // FORM
  // --------------------------------
  private buildForm() {
    this.formFinanciero = this.fb.group({
      rangos: this.fb.array(
        this.rangos.map((r) => this.createRow(r)),
        { validators: rangosAtLeastOneValidator() },
      ),

      activos: [null, Validators.required],
      pasivos: [null, Validators.required],

      origenFondos: this.fb.group({
        ahorros: [false],
        salarios: [false],
        honorarios: [false],
        otros: [false],
        otrosDescripcion: [''],
        ventaActivos: [false],
        arrendamientos: [false],
        rendimientos: [false],
        comisiones: [false],
      }),

      descripcionOtrasFuentes: ['', Validators.maxLength(200)],

      /** =========================
       *  MONEDA EXTRANJERA
       *  ========================= */
      monedaExtranjera: this.fb.group(
        {
          realizaOperaciones: [null, Validators.required],
          exportaciones: [false],
          importaciones: [false],
          transferencias: [false],
          pagosServicios: [false],
          inversiones: [false],
          prestamos: [false],
          giros: [false],
        },
        { validators: monedaExtranjeraValidator() },
      ),

      /** =========================
       *  PEP
       *  ========================= */
      pep: this.fb.group({
        esPep: [null, Validators.required],
        cargo: [''],
        fechaInicio: [''],
        fechaFin: [''],

        representaONG: [null, Validators.required],
        cargoONG: [''],
        fechaInicioONG: [''],
        fechaFinONG: [''],

        vinculoPep: [null, Validators.required],
        nombrePep: [''],
        cargoPep: [''],
        parentesco: [''],
      }),
    });
  }

  private createRow(rango: string): FormGroup {
    return this.fb.group({
      rango: [rango],
      ingresos: [false],
      egresos: [false],
      otros: [false],
    });
  }

  get rows(): FormArray {
    return this.formFinanciero.get('rangos') as FormArray;
  }

  get origenFondos(): FormGroup {
    return this.formFinanciero.get('origenFondos') as FormGroup;
  }

  // --------------------------------
  // GETTERS DE ERROR (leen el validator del FormArray/FormGroup)
  // --------------------------------
  get ingresosRequired(): boolean {
    return this.rows.hasError('ingresosRequired');
  }

  get egresosRequired(): boolean {
    return this.rows.hasError('egresosRequired');
  }

  get operacionMonedaRequired(): boolean {
    const me = this.formFinanciero.get('monedaExtranjera');
    return me?.hasError('operacionRequired') ?? false;
  }

  // --------------------------------
  // EXCLUSIVIDAD POR COLUMNA
  // --------------------------------
  onCheckChange(index: number, column: 'ingresos' | 'egresos' | 'otros') {
    this.showRangeErrors = true;

    this.rows.controls.forEach((row, i) => {
      if (i !== index) {
        row.get(column)?.setValue(false, { emitEvent: false });
      }
    });

    // Fuerza revalidación + emisión real de statusChanges tras la cascada silenciosa.
    this.rows.updateValueAndValidity();
  }

  // --------------------------------
  // LISTENERS
  // --------------------------------
  private listenOtrosOrigen() {
    this.origenFondos.get('otros')?.valueChanges.subscribe((checked) => {
      const desc = this.origenFondos.get('otrosDescripcion');

      if (checked) {
        desc?.setValidators([Validators.required]);
      } else {
        desc?.clearValidators();
        desc?.setValue('');
      }

      desc?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private listenMonedaExtranjera() {
    const me = this.formFinanciero.get('monedaExtranjera') as FormGroup;

    me.get('realizaOperaciones')?.valueChanges.subscribe((val) => {
      this.showMonedaErrors = true;

      if (!val) {
        Object.keys(me.controls).forEach((key) => {
          if (key !== 'realizaOperaciones') {
            me.get(key)?.setValue(false, { emitEvent: false });
          }
        });
      }

      // Fuerza revalidación + emisión tras la cascada silenciosa.
      me.updateValueAndValidity();
    });

    const operaciones = [
      'exportaciones',
      'importaciones',
      'transferencias',
      'pagosServicios',
      'inversiones',
      'prestamos',
      'giros',
    ];
    operaciones.forEach((op) => {
      me.get(op)?.valueChanges.subscribe(() => {
        this.showMonedaErrors = true;
      });
    });
  }

  private listenPep() {
    const pep = this.formFinanciero.get('pep') as FormGroup;

    pep.get('esPep')?.valueChanges.subscribe((val) => {
      this.toggleValidators(pep, ['cargo', 'fechaInicio', 'fechaFin'], val);
    });

    pep.get('representaONG')?.valueChanges.subscribe((val) => {
      this.toggleValidators(
        pep,
        ['cargoONG', 'fechaInicioONG', 'fechaFinONG'],
        val,
      );
    });

    pep.get('vinculoPep')?.valueChanges.subscribe((val) => {
      this.toggleValidators(pep, ['nombrePep', 'cargoPep', 'parentesco'], val);
    });
  }

  private toggleValidators(
    group: FormGroup,
    controls: string[],
    enable: boolean,
  ) {
    controls.forEach((c) => {
      const control = group.get(c);
      if (enable) {
        control?.setValidators(Validators.required);
      } else {
        control?.clearValidators();
        control?.setValue('');
      }
      control?.updateValueAndValidity({ emitEvent: false });
    });

    // Los toggles individuales no propagan statusChanges del grupo: forzar refresco.
    group.updateValueAndValidity();
  }

  private persistFormChanges() {
    this.formFinanciero.valueChanges.subscribe((value) => {
      this.localStorageService.save('sarlaft_form', value);
    });
  }

  private restoreForm() {
    const savedData = this.localStorageService.load('sarlaft_form');

    if (savedData) {
      this.formFinanciero.patchValue(savedData, { emitEvent: false });

      const pep = this.formFinanciero.get('pep') as FormGroup;

      this.toggleValidators(
        pep,
        ['cargo', 'fechaInicio', 'fechaFin'],
        pep.get('esPep')?.value === true,
      );

      this.toggleValidators(
        pep,
        ['cargoONG', 'fechaInicioONG', 'fechaFinONG'],
        pep.get('representaONG')?.value === true,
      );

      this.toggleValidators(
        pep,
        ['nombrePep', 'cargoPep', 'parentesco'],
        pep.get('vinculoPep')?.value === true,
      );
    }
  }
}